/**
 * Database search API — companies + people paginated browse.
 */
import { apiClient } from '@/services/api/client';

export interface CompanyRow {
  id: string;
  name: string;
  /** Human-readable normalised name; falls back to `name` when not backfilled. */
  display_name?: string | null;
  registration_number: string;
  jurisdiction_code: string;
  status: string;
  company_type?: string | null;
  incorporated_on?: string | null;
  dissolved_on?: string | null;
  industry_codes?: string[] | null;
  fca_status?: string | null;
  lei?: string | null;
  /** Bare web domain (e.g. `expedition.capital`); powers logo.dev fallback. */
  domain?: string | null;
  /** BrightData LinkedIn company logo URL when enrichment ran. */
  logo_url?: string | null;
}

export interface CompaniesSearchResponse {
  results: CompanyRow[];
  pagination: { total: number; offset: number; limit: number };
}

export type PersonCareerKind = 'officership' | 'linkedin' | 'education';

export interface PersonCareerFirm {
  /** `officership` rows have a real `companies.id` UUID; `linkedin`
   *  rows do too when the LE record resolved to a known company,
   *  otherwise it's an `le-<hash>` synthetic. `education` rows are
   *  always `edu-<hash>`. The frontend must only treat `id` as
   *  routable when it parses as a UUID. */
  kind: PersonCareerKind;
  id: string;
  name: string;
  display_name?: string | null;
  domain?: string | null;
  logo_url?: string | null;
  year_from?: number | null;
  year_to?: number | null;
  is_current: boolean;
}

export interface PersonRow {
  persona_id: string;
  persona_kind: 'cluster' | 'person';
  canonical_name: string;
  member_count: number;
  /** Best photo across cluster: web-scrape first, BrightData avatar fallback. */
  photo_url?: string | null;
  /** Most recent active title — LinkedIn experience preferred, else last officership role. */
  current_title?: string | null;
  /** Current organisation — paired with `current_title` and picked from
   *  the same source row. `current_org_id` is a `companies.id` UUID
   *  when the role resolved to a known company; null for LinkedIn-only
   *  entries that never matched a company in the graph. */
  current_org_name?: string | null;
  current_org_id?: string | null;
  current_org_logo_url?: string | null;
  current_org_domain?: string | null;
  /** Free-text city/country composite from LinkedIn enrichment. */
  location?: string | null;
  /** Contact pointers picked across cluster siblings. */
  linkedin_url?: string | null;
  twitter_url?: string | null;
  email?: string | null;
}

export interface PeopleSearchResponse {
  results: PersonRow[];
  pagination: { total: number; offset: number; limit: number };
}

/** Response of GET /v1/lineage/people/career-strips — keyed by persona_id
 *  for O(1) lookup per row. Personas absent from the dict (or with empty
 *  arrays) have no career data on file. */
export interface CareerStripsResponse {
  career_strips: Record<string, PersonCareerFirm[]>;
}

// Cluster review queue — pending_review company clusters surfaced
// for analyst approve/reject decisions.

export type CompanyClusterStatus =
  | 'pending_review'
  | 'auto_merged'
  | 'human_approved'
  | 'human_rejected'
  | 'split';

export interface CompanyClusterListItem {
  id: string;
  canonical_name?: string | null;
  match_method?: string | null;
  match_confidence?: number | null;
  status: CompanyClusterStatus;
  member_count: number;
  members: ClusterCompanyMember[];
}

export interface CompanyClustersResponse {
  results: CompanyClusterListItem[];
  pagination: { total: number; offset: number; limit: number };
}

export async function listCompanyClusters(params: {
  status?: CompanyClusterStatus;
  offset?: number;
  limit?: number;
}): Promise<CompanyClustersResponse> {
  return apiClient.get<CompanyClustersResponse>('/v1/lineage/clusters/companies', {
    status: params.status,
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

export async function fetchCompanyClusterDetail(
  clusterId: string,
): Promise<CompanyClusterListItem> {
  return apiClient.get<CompanyClusterListItem>(
    `/v1/lineage/clusters/companies/${clusterId}`,
  );
}

export async function updateCompanyClusterStatus(
  clusterId: string,
  payload: { status: 'human_approved' | 'human_rejected' | 'split'; notes?: string },
): Promise<CompanyClusterListItem> {
  return apiClient.patch<CompanyClusterListItem>(
    `/v1/lineage/clusters/companies/${clusterId}`,
    payload,
  );
}

/** Allowed values for the Companies "Enriched" multi-select filter. Each
 *  flag adds an EXISTS predicate on the backend; selections AND together. */
export type CompanyEnrichedFlag = 'website' | 'scrape' | 'linkedin' | 'crunchbase';

/** Allowed values for the People "Enriched" multi-select filter. */
export type PersonEnrichedFlag = 'team' | 'linkedin' | 'crunchbase';

/** Tier filter for the Companies list. 'tracked' (default) hides
 *  portfolio holdings + historic-employer rows; 'reference' shows only
 *  those; 'all' disables the filter. */
export type CompaniesTierFilter = 'tracked' | 'reference' | 'all';

export async function searchCompanies(params: {
  q?: string;
  jurisdiction_code?: string;
  status?: string;
  fca_registered?: boolean;
  regime?: string;
  has_lei?: boolean;
  source?: string;
  enriched?: CompanyEnrichedFlag[];
  tracking_tier?: CompaniesTierFilter;
  offset?: number;
  limit?: number;
}): Promise<CompaniesSearchResponse> {
  return apiClient.get<CompaniesSearchResponse>('/v1/lineage/search/companies', {
    q: params.q,
    jurisdiction_code: params.jurisdiction_code,
    status: params.status,
    fca_registered: params.fca_registered ? true : undefined,
    regime: params.regime,
    has_lei: params.has_lei === true ? true : params.has_lei === false ? false : undefined,
    source: params.source,
    enriched: params.enriched && params.enriched.length > 0 ? params.enriched : undefined,
    tracking_tier: params.tracking_tier ?? 'tracked',
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

export async function searchPeople(params: {
  q?: string;
  enriched?: PersonEnrichedFlag[];
  offset?: number;
  limit?: number;
}): Promise<PeopleSearchResponse> {
  return apiClient.get<PeopleSearchResponse>('/v1/lineage/search/people', {
    q: params.q,
    enriched: params.enriched && params.enriched.length > 0 ? params.enriched : undefined,
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

/** Batched career-strip fetch for the People list page. Pass any number of
 *  persona ids; the function chunks into ≤100-id batches (backend limit) and
 *  merges the results. Response is keyed by persona_id for direct per-row lookup. */
export async function fetchPeopleCareerStrips(
  personaIds: string[],
): Promise<CareerStripsResponse> {
  if (personaIds.length === 0) return { career_strips: {} };
  const CHUNK = 100;
  if (personaIds.length <= CHUNK) {
    return apiClient.get<CareerStripsResponse>('/v1/lineage/people/career-strips', {
      ids: personaIds.join(','),
    });
  }
  const chunks: string[][] = [];
  for (let i = 0; i < personaIds.length; i += CHUNK) {
    chunks.push(personaIds.slice(i, i + CHUNK));
  }
  const results = await Promise.all(
    chunks.map((batch) =>
      apiClient.get<CareerStripsResponse>('/v1/lineage/people/career-strips', {
        ids: batch.join(','),
      }),
    ),
  );
  return { career_strips: Object.assign({}, ...results.map((r) => r.career_strips)) };
}

// ---------------------------------------------------------------------------
// Detail endpoints (Tier 1)
// ---------------------------------------------------------------------------

export interface PreviousName {
  name: string;
  valid_from?: string | null;
  valid_to?: string | null;
}

export interface RegisteredOffice {
  address_line_1?: string | null;
  address_line_2?: string | null;
  locality?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  [key: string]: unknown;
}

export interface ClusterCompanyMember {
  id: string;
  name: string;
  display_name?: string | null;
  registration_number: string;
  jurisdiction_code: string;
  source_code?: string | null;
  lei?: string | null;
}

export interface CompanyCluster {
  id: string;
  canonical_name?: string | null;
  match_method?: string | null;
  match_confidence?: number | null;
  status: string;
  member_count: number;
  members: ClusterCompanyMember[];
}

/** One entity in a cross-border affiliate group — a legally DISTINCT
 * company (different jurisdiction/registration) linked via a corroborating
 * signal (today: shared domain), never merged into a cluster row. */
export interface AffiliateMember {
  id: string;
  name: string;
  display_name?: string | null;
  registration_number: string;
  jurisdiction_code: string;
  source_code?: string | null;
  lei?: string | null;
  domain?: string | null;
}

export interface AffiliateGroup {
  members: AffiliateMember[];
}

export interface RegulatorAuthorisation {
  source_code: string;
  kind: string;
  authorisation_value: string;
  country_code?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface FundVehicleSummary {
  id: string;
  external_id: string;
  source_code: string;
  name: string;
  fund_type_internal?: string | null;
  fund_type_raw?: string | null;
  jurisdiction?: string | null;
  is_master_fund: boolean;
  is_feeder_fund: boolean;
  master_fund_id?: string | null;
  gross_asset_value_usd?: number | null;
  net_asset_value_usd?: number | null;
  inception_date?: string | null;
  lei?: string | null;
}

export interface CompanyWebProfile {
  description?: string | null;
  address_text?: string | null;
  phone?: string | null;
  emails?: string[];
  linkedin_url?: string | null;
  twitter_url?: string | null;
  facebook_url?: string | null;
  youtube_url?: string | null;
  instagram_url?: string | null;
  other_social_urls?: Record<string, string>;
  discovery_method?: string | null;
  verification_confidence?: number | null;
  last_scraped_at?: string | null;
}

export interface WebScrapedPerson {
  id: string;
  name: string;
  title?: string | null;
  bio?: string | null;
  linkedin_url?: string | null;
  twitter_url?: string | null;
  email?: string | null;
  photo_url?: string | null;
  source_url?: string | null;
  source_page_type?: string | null;
  extractor_version?: string | null;
  er_person_id?: string | null;
  /** Server-side prediction. NULL when an observed email exists, when
   *  the row is in the Advisory bucket, or when the firm has no
   *  detected pattern. UI must surface as unvalidated. */
  predicted_email?: string | null;
  /** Firm-level pattern metadata, duplicated onto every row of the
   *  same company. Drives the analyst-facing tooltip ("predicted via
   *  X@, inferred from Y/Z colleagues"). */
  firm_email_pattern_key?: string | null;
  firm_email_pattern_domain?: string | null;
  firm_email_pattern_support?: number | null;
  firm_email_pattern_observed?: number | null;
}

export interface CompanyLinkedInProfile {
  about?: string | null;
  slogan?: string | null;
  industries?: string[] | null;
  organization_type?: string | null;
  company_size?: string | null;
  employees_in_linkedin?: number | null;
  followers_count?: number | null;
  founded_year?: number | null;
  hq_country?: string | null;
  hq_city?: string | null;
  formatted_locations?: string[] | null;
  directions_urls?: string[] | null;
  employees_count_change_yoy_pct?: number | null;
}

export interface FounderEntry {
  persona: {
    id: string;
    kind: 'cluster' | 'person';
    canonical_name: string;
    member_count: number;
  };
  position_title?: string | null;
  photo_url?: string | null;
  date_from_year?: number | null;
}

export interface OfficeLocation {
  id: string;
  ordinal: number;
  formatted_location: string;
  city?: string | null;
  region?: string | null;
  country_iso2?: string | null;
  directions_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CompanyDetail {
  id: string;
  name: string;
  /** Human-readable normalised name; falls back to `name` when not backfilled. */
  display_name?: string | null;
  registration_number: string;
  jurisdiction_code: string;
  status: string;
  company_type?: string | null;
  incorporated_on?: string | null;
  dissolved_on?: string | null;
  industry_codes?: string[] | null;
  registered_office?: RegisteredOffice | null;
  previous_names: PreviousName[];
  officer_count_total: number;
  officer_count_current: number;

  // Tier classification (added 2026-05-17)
  tracking_tier?: 'tracked' | 'reference';

  // Regulator-data enrichment (added 2026-04-29)
  lei?: string | null;
  cluster?: CompanyCluster | null;
  // Cross-border affiliate linking (added 2026-07-08) — legally distinct
  // entities in the same commercial group, deliberately not merged into
  // `cluster`. People/signals/data across cluster + affiliate members are
  // already pooled into this one response, regardless of which member id
  // was requested.
  affiliate_group?: AffiliateGroup | null;
  // False when a different cluster/affiliate member was chosen to
  // represent the group's display identity (name/logo/domain/etc.) —
  // `id` above always stays the requested id.
  is_canonical?: boolean;
  sources?: string[];
  authorisations?: RegulatorAuthorisation[];
  funds_managed?: FundVehicleSummary[];

  // Web-enrichment surfacing (added 2026-05-03)
  website?: string | null;
  domain?: string | null;
  logo_url?: string | null;
  linkedin_profile?: CompanyLinkedInProfile | null;
  web_profile?: CompanyWebProfile | null;
  office_locations?: OfficeLocation[];
  founders?: FounderEntry[];
  scraped_team?: WebScrapedPerson[];

  // Portfolio + Crunchbase companion streams (added 2026-05-07)
  portfolio_overview?: PortfolioOverview | null;
  crunchbase_funds?: CrunchbaseFundSummary[];
  news?: FirmNewsItem[];
  crunchbase_employees_current?: CrunchbaseEmployeeEntry[];

  // Inbound board seats (added 2026-05-16) — people from PE/GE/VC
  // firms who hold a seat on THIS company's board / advisory /
  // operating committee. Populated for portfolio companies; empty
  // for the firms themselves.
  inbound_board_seats?: InboundBoardSeat[];
  // True when this company has at least one row in firm_aggregate_strength
  // — drives the Network tab visibility on the detail page.
  has_network_ties?: boolean;
}

/** CB-listed employee with on-scraped-team match flag. Frontend uses
 *  `on_scraped_team=false` rows to (a) augment current-team display
 *  when scrape is sparse, or (b) display in alumni when scrape is
 *  comprehensive — see `crunchbase_alumni` on alumni response which
 *  is only populated when the scraped-team threshold is met. */
export interface CrunchbaseEmployeeEntry {
  id: string;
  name: string;
  title?: string | null;
  image_url?: string | null;
  cb_permalink: string;
  is_founder: boolean;
  is_current: boolean;
  on_scraped_team: boolean;
}

/** Merged-by-target portfolio observation. `sources` is the list of
 *  lanes that saw this target — `['portfolio_page','crunchbase']`
 *  means corroborated. */
export interface PortfolioCompanyMerged {
  target_name: string;
  target_name_normalized: string;
  target_company_id?: string | null;
  mapped_company_id?: string | null;
  sources: string[];
  is_corroborated: boolean;
  description_short?: string | null;
  website?: string | null;
  linkedin_url?: string | null;
  logo_url?: string | null;
  fund?: string | null;
  sector?: string | null;
  hq_country?: string | null;
  status?: string | null;
}

export interface PortfolioSummary {
  total: number;
  corroborated: number;
  sample: PortfolioCompanyMerged[];
}

export interface PortfolioCoInvestedFirm {
  firm_id: string;
  firm_name: string;
  firm_display_name?: string | null;
  shared_count: number;
}

/** Rich Overview-tab payload — stats + curated sections.
 *  Renders only sections that are populated. */
export interface PortfolioOverview {
  total: number;
  current_count: number;
  exited_count: number;
  earliest_entry_year?: number | null;
  sector_count: number;
  recent_additions: PortfolioCompanyMerged[];
  notable_exits: PortfolioCompanyMerged[];
  co_invested_with: PortfolioCoInvestedFirm[];
}

/** Rich portfolio row — one per canonical target, joined to
 *  portfolio_targets, with co-investors (other firms in our DB
 *  with the same target) and Lane 5 board seats from this firm. */
export interface PortfolioBoardMember {
  name: string;
  role?: string | null;
}

export interface PortfolioCoInvestor {
  firm_id: string;
  firm_name: string;
  firm_display_name?: string | null;
  cluster_id?: string | null;
}

export interface PortfolioBoardSeat {
  person_name: string;
  person_id?: string | null;
  role_title?: string | null;
  role_band: string;            // 'board' | 'advisory' | 'operating' | 'investor'
  from_year?: number | null;
  to_year?: number | null;
  is_active: boolean;
}

/** A board / advisory / operating role a persona holds at a company
 *  *other than* their primary employer firm. Surfaced on persona
 *  detail under "Concurrent roles". `firm_*` names the primary firm
 *  whose tenure overlaps this role. */
export interface ConcurrentRoleSeat {
  seat_id: string;
  firm_id: string;
  firm_name?: string | null;
  firm_display_name?: string | null;
  firm_logo_url?: string | null;
  target_company_id?: string | null;
  target_company_name: string;
  target_company_linkedin_url?: string | null;
  target_company_industry?: string | null;
  target_company_country_iso2?: string | null;
  role_title?: string | null;
  role_band: string;             // 'board' | 'advisory' | 'operating'
  from_year?: number | null;
  to_year?: number | null;
  is_active: boolean;
  confidence?: number | null;
}

/** Inverse of `ConcurrentRoleSeat`: a person from a PE/GE/VC firm
 *  who holds a seat on THIS company's board. Rendered on portfolio-
 *  company detail under "Investors on board". */
export interface InboundBoardSeat {
  seat_id: string;
  firm_id: string;
  firm_name?: string | null;
  firm_display_name?: string | null;
  firm_logo_url?: string | null;
  person_id?: string | null;
  person_name?: string | null;
  person_role_at_firm?: string | null;
  role_title?: string | null;
  role_band: string;             // 'board' | 'advisory' | 'operating'
  from_year?: number | null;
  to_year?: number | null;
  is_active: boolean;
  confidence?: number | null;
}

export interface PortfolioCompanyRich {
  target_id: string;
  target_name: string;
  target_name_normalized: string;
  mapped_company_id?: string | null;
  description_short?: string | null;
  website?: string | null;
  linkedin_url?: string | null;
  logo_url?: string | null;
  sector?: string | null;
  hq_country?: string | null;
  fund?: string | null;
  stage?: string | null;
  status?: string | null;        // 'current' | 'exited' | null
  entry_year?: number | null;
  exit_year?: number | null;
  exit_type?: string | null;     // 'ipo' | 'acquired' | 'sold' | 'other' | null
  cb_lead_investor?: boolean | null;
  cb_funding_round?: string | null;
  investor_firm_count: number;
  is_corroborated: boolean;
  sources: string[];
  board_members: PortfolioBoardMember[];
  investment_team: PortfolioBoardMember[];
  co_investors: PortfolioCoInvestor[];
  board_seats: PortfolioBoardSeat[];
}

export interface PortfolioListResponse {
  company: { id: string; name: string };
  items: PortfolioCompanyRich[];
  pagination: { total: number; offset: number; limit: number };
}

export interface CrunchbaseFundSummary {
  id: string;
  fund_name: string;
  image_id?: string | null;
}

export interface FirmNewsItem {
  id: string;
  title: string;
  url: string;
  publisher?: string | null;
  date_published?: string | null;
  thumbnail_url?: string | null;
  organization?: string | null;
}

export interface CrunchbaseAlumniEntry {
  id: string;
  name: string;
  title?: string | null;
  image_url?: string | null;
  cb_permalink: string;
  is_founder: boolean;
}

export interface ClusterMember {
  id: string;
  full_name: string;
  source_slug?: string | null;
  source_url?: string | null;
  given_names?: string | null;
  surname?: string | null;
  date_of_birth_year?: number | null;
  date_of_birth_month?: number | null;
  nationality?: string | null;
  country_of_residence?: string | null;
  occupation?: string | null;
}

/** Web-scraped appearance of this persona on a firm's website. One
 *  row per (firm, scrape) where the persona's `er_person_id` was
 *  matched. Ordered by extracted_at DESC so the freshest appearance
 *  is first — useful for picking the photo/bio to render. */
export interface TeamAppearance {
  company_id: string;
  company_name: string;
  company_display_name?: string | null;
  company_country_code?: string | null;
  observed_name?: string | null;
  title?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  source_url: string;
  source_page_type: string;
  linkedin_url?: string | null;
  email?: string | null;
  extracted_at?: string | null;
}

export interface Education {
  institution_name: string;
  institution_url?: string | null;
  institution_logo_url?: string | null;
  institution_country_iso2?: string | null;
  degree?: string | null;
  description?: string | null;
  date_from_year?: number | null;
  date_to_year?: number | null;
  order_in_profile: number;
}

export interface PersonaDetail {
  id: string;
  kind: 'cluster' | 'person';
  canonical_name: string;
  dob_year?: number | null;
  dob_month?: number | null;
  nationality?: string | null;
  country_of_residence?: string | null;
  occupation?: string | null;
  linkedin_url?: string | null;
  member_count: number;
  members: ClusterMember[];
  team_appearances?: TeamAppearance[];
  education?: Education[];
  concurrent_roles?: ConcurrentRoleSeat[];
  // True when this persona has at least one row in person_firm_strength
  // — drives the embedded radial visibility on the persona detail page.
  has_network_ties?: boolean;
}

export interface OfficerEntry {
  officership_id: string;
  role: string;
  role_detail?: string | null;
  appointed_on?: string | null;
  resigned_on?: string | null;
  is_current: boolean;
  share_percentage?: number | null;
  nature_of_control?: string[] | null;
  persona: {
    id: string;
    kind: 'cluster' | 'person';
    canonical_name: string;
    dob_year?: number | null;
    dob_month?: number | null;
    member_count: number;
  };
  observed_as: string;
  /** Which entity in the profile's pooling group (self, cluster member, or
   * affiliate) this officership is at — a per-jurisdiction legal fact, so
   * pooled rows stay attributed rather than merged into one list. */
  company_id?: string | null;
  company_name?: string | null;
}

export interface LinkedInTeamEntry {
  id: string;
  persona: {
    id: string;
    kind: 'cluster' | 'person';
    canonical_name: string;
    member_count: number;
  };
  position_title?: string | null;
  department?: string | null;
  management_level?: string | null;
  location?: string | null;
  date_from_year?: number | null;
  date_from_month?: number | null;
  date_to_year?: number | null;
  date_to_month?: number | null;
  duration_months?: number | null;
  is_active_experience: boolean;
  persona_photo_url?: string | null;
}

export interface OfficersResponse {
  company: {
    id: string;
    name: string;
    display_name?: string | null;
    registration_number: string;
    jurisdiction_code: string;
    status: string;
    incorporated_on?: string | null;
    dissolved_on?: string | null;
  };
  officers: OfficerEntry[];
  linkedin_team: LinkedInTeamEntry[];
  crunchbase_alumni?: CrunchbaseAlumniEntry[];
  pagination: { total: number; offset: number; limit: number };
}

export async function fetchCompanyDetail(id: string): Promise<CompanyDetail> {
  return apiClient.get<CompanyDetail>(`/v1/lineage/companies/${id}`);
}

export async function fetchPersonaDetail(id: string): Promise<PersonaDetail> {
  return apiClient.get<PersonaDetail>(`/v1/lineage/people/${id}`);
}

export async function fetchCompanyOfficers(
  id: string,
  params: { current_only?: boolean; offset?: number; limit?: number } = {}
): Promise<OfficersResponse> {
  return apiClient.get<OfficersResponse>(`/v1/lineage/companies/${id}/officers`, {
    current_only: params.current_only,
    offset: params.offset ?? 0,
    limit: params.limit ?? 100,
  });
}

export async function fetchCompanyAlumni(
  id: string,
  params: { offset?: number; limit?: number } = {}
): Promise<OfficersResponse> {
  return apiClient.get<OfficersResponse>(`/v1/lineage/companies/${id}/alumni`, {
    offset: params.offset ?? 0,
    limit: params.limit ?? 100,
  });
}

export async function fetchCompanyPortfolio(
  id: string,
  params: { offset?: number; limit?: number } = {}
): Promise<PortfolioListResponse> {
  return apiClient.get<PortfolioListResponse>(
    `/v1/lineage/companies/${id}/portfolio`,
    {
      offset: params.offset ?? 0,
      limit: params.limit ?? 50,
    }
  );
}

// ---------------------------------------------------------------------------
// Persona-side career + connections
// ---------------------------------------------------------------------------

export interface PositionCompany {
  id: string;
  name: string;
  display_name?: string | null;
  registration_number: string;
  jurisdiction_code: string;
  status: string;
  incorporated_on?: string | null;
  dissolved_on?: string | null;
  domain?: string | null;
  logo_url?: string | null;
}

export interface Position {
  officership_id: string;
  role: string;
  role_detail?: string | null;
  appointed_on?: string | null;
  resigned_on?: string | null;
  is_current: boolean;
  share_percentage?: number | null;
  nature_of_control?: string[] | null;
  company: PositionCompany;
  observed_as: string;
}

export interface LinkedInRole {
  id: string;
  position_title?: string | null;
  department?: string | null;
  management_level?: string | null;
  location?: string | null;
  description?: string | null;
  date_from_year?: number | null;
  date_from_month?: number | null;
  date_to_year?: number | null;
  date_to_month?: number | null;
  duration_months?: number | null;
  is_active_experience: boolean;
  company_id?: string | null;
  company_linkedin_url?: string | null;
  company_website?: string | null;
  company_logo_url?: string | null;
  company_name?: string | null;
  company_display_name?: string | null;
  company_industry?: string | null;
  company_size_range?: string | null;
  company_employees_count?: number | null;
  company_hq_country_iso2?: string | null;
}

export interface CareerResponse {
  persona: {
    id: string;
    kind: 'cluster' | 'person';
    canonical_name: string;
    dob_year?: number | null;
    dob_month?: number | null;
    member_count: number;
  };
  positions: Position[];
  linkedin_roles: LinkedInRole[];
  pagination: { total: number; offset: number; limit: number };
}

export interface Connection {
  persona: {
    id: string;
    kind: 'cluster' | 'person';
    canonical_name: string;
    dob_year?: number | null;
    dob_month?: number | null;
    member_count: number;
  };
  shared_company_count: number;
  shared_companies: PositionCompany[];
}

export interface ConnectionsResponse {
  persona: CareerResponse['persona'];
  connections: Connection[];
  pagination: { total: number; offset: number; limit: number };
}

export async function fetchPersonaCareer(
  id: string,
  params: { offset?: number; limit?: number } = {}
): Promise<CareerResponse> {
  return apiClient.get<CareerResponse>(`/v1/lineage/people/${id}/career`, {
    offset: params.offset ?? 0,
    limit: params.limit ?? 100,
  });
}

export async function fetchPersonaConnections(
  id: string,
  params: { offset?: number; limit?: number } = {}
): Promise<ConnectionsResponse> {
  return apiClient.get<ConnectionsResponse>(`/v1/lineage/people/${id}/connections`, {
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

// ---------------------------------------------------------------------------
// LinkedIn-derived relationships (PR #5 endpoint)
// ---------------------------------------------------------------------------

export interface LinkedInRelationship {
  counterparty_id: string;
  counterparty_name: string;
  counterparty_linkedin_url?: string | null;
  counterparty_photo_url?: string | null;
  counterparty_current_company_name?: string | null;
  counterparty_headline?: string | null;
  score_symmetric: number;
  score_outbound: number;
  score_inbound: number;
  edge_count: number;
  signal_breakdown: Record<string, number>;
  last_edge_at?: string | null;
}

export interface LinkedInRelationshipsResponse {
  person_id: string;
  relationships: LinkedInRelationship[];
}

export async function fetchPersonaRelationships(
  id: string,
  params: { limit?: number; exclude_current_coworkers?: boolean } = {}
): Promise<LinkedInRelationshipsResponse> {
  return apiClient.get<LinkedInRelationshipsResponse>(`/v1/lineage/relationships/${id}`, {
    limit: params.limit ?? 20,
    exclude_current_coworkers: params.exclude_current_coworkers ?? false,
  });
}
